<template>
  <layout-envelope class="text-color-regular" :style="{ background }">
    <top-small>
      <template v-slot:left
        ><back-button-government-home class="text-interaction-dark"
      /></template>
      <template v-slot:right v-if="isMobile">
        <font-awesome-icon
          :icon="['fas', 'share-nodes']"
          size="xl"
          class="text-interaction-dark"
          @click="share"
        />
      </template>
    </top-small>

    <div
      class="min-h-0 flex-grow"
      style="margin: 0 -0.84rem -1.624rem -0.84rem;"
      v-if="!isLoading"
      v-statusbar:#fff
    >
      <div
        class="lg:max-h-full flex flex-col lg:flex-row gap-16"
        style="padding: 0 0.84rem 1.624rem 0.84rem"
      >
        <div class="w-full lg:w-1/3">
          <h1 class="text-chplus-title-l leading-none font-normal">
            {{ formattedId }}
          </h1>
          <div class="text-chplus-s mb-6 text-interaction-dark">
            {{ $t("views.government.vote.affair_number") }}
          </div>
          <div
            v-if="affair.peopleVote != 'no'"
            class="bg-color-user leading-none rounded-lg text-chplus-s text-white uppercase mb-2 text-left px-2 py-1"
          >
            <span class="font-bold">{{
              $t("components.tags.people_vote.label")
            }}</span>
            <span>{{
              $t(`components.tags.people_vote.${affair.peopleVote}`)
            }}</span>
          </div>
          <div class="vote-item-tags flex flex-wrap gap-2">
            <div v-for="(topic, i) in affair.topics" :key="i">
              <vote-item-tag :topic="topic" inline />
            </div>
          </div>
          <div
            class="vote-item-title text-chplus-candidate-info text-left my-1"
          >
            {{ affair.title }}
          </div>
          <div class="flex text-chplus-s text-color-candidate font-bold">
            <div
              class="vote-item-business-type font-bold text-left leading-tight my-auto"
            >
              {{ affair.affairType.name }}
            </div>
          </div>
          <div class="text-chplus-s text-left" style="margin-bottom: 3.5rem;">
            {{
              $t("views.government.vote.deposit_date", {
                date: formattedDate(affair.depositDate)
              })
            }}
          </div>
        </div>
        <div
          class="w-full lg:w-2/3 lg:px-4 lg:overflow-y-auto overflow-x-hidden flex flex-col"
        >
          <div
            v-for="(paragraph, index) in prettyParagraphs"
            :key="index"
            class="text-chplus-regular"
            :style="{ color: 'var(--color-candidate)' }"
          >
            <div
              class="text-chplus-title text-white font-bold cursor-pointer relative"
              style="margin: 0 -0.84rem 1.75rem -0.84rem;"
              @click="toggleParagraph(index)"
            >
              <div
                class="w-full absolute bg-highlight"
                style="height: 2px; top: 50%; z-index: 0;"
              ></div>
              <div
                class="rounded-lg relative bg-color-candidate"
                style="margin: 0 0.84rem; z-index: 2; padding: 2px 0;"
              >
                <span>{{ paragraph.title }}</span>
                <div class="absolute right-0 top-0 pr-2">
                  <icon-angle
                    :direction="hideFullParagraph[index] ? 'down' : 'up'"
                  />
                </div>
              </div>
            </div>
            <div
              v-if="!hideFullParagraph[index]"
              class="text-justify text-chplus-candidate-info leading-tight cursor-pointer"
              style="padding-bottom: 3.5rem;"
              :class="{
                [paragraph.class]: true
              }"
              @click="toggleShow"
            >
              <div v-for="(subparagraph, i) of paragraph.content" :key="i">
                <div v-if="i !== 0" class="affair-paragraph-separator"></div>
                <div
                  :class="{
                    'show-excerpt': !showSubParagraph[subparagraph.id]
                  }"
                  v-html="subparagraph.outerHTML"
                ></div>
              </div>
            </div>
          </div>
          <div
            class="text-chplus-title text-white font-bold relative cursor-pointer"
            style="margin: 0 -0.84rem"
            @click="toggleParagraph('votes')"
          >
            <div
              class="w-full absolute bg-highlight"
              style="height: 2px; top: 50%; z-index: 0;"
            ></div>
            <div
              class="rounded-lg relative bg-color-candidate"
              style="margin: 0 0.84rem; z-index: 2; padding: 2px 0;"
            >
              <span>{{ $t("views.government.vote.votes") }}</span>
              <span class="font-thin"> ({{ affair.votes.length }})</span>
              <div class="absolute right-0 top-0 pr-2">
                <icon-angle
                  :direction="hideFullParagraph['votes'] ? 'down' : 'up'"
                />
              </div>
            </div>
          </div>
          <div v-if="!hideFullParagraph['votes']" class="flex flex-col gap-4">
            <div>
              <div style="margin-bottom: 2.45rem;">
                <navigation-government
                  :title="$t('views.government-home.national_council')"
                  :numMembers="200"
                  :canChange="false"
                  class="my-6"
                />
              </div>
              <navigation-arrow
                title=""
                :canChange="affair.votes?.length > 1"
                @previous="previousVote()"
                @next="nextVote()"
                class="my-4"
                styling="text-contrast-light bg-white vote-switch"
                full
              >
                {{ voteItem.divisionText }}
              </navigation-arrow>
              <div class="text-chplus-s text-left">
                {{ formattedDate(voteItem.date) }}
              </div>
              <div
                class="text-chplus-candidate-info text-color-candidate text-left leading-tight"
              >
                {{ voteItem.submissionText }}
              </div>
              <div
                class="yesno-container text-chplus-candidate-info text-color-candidate text-left leading-none my-4"
              >
                <div class="flex gap-1">
                  <div class="h-5 w-5 rounded-md bg-vote-yes"></div>
                  <span>{{ yes }}:</span>
                </div>
                <div>"{{ voteItem.meaningYes }}"</div>
                <div class="flex gap-1">
                  <div class="h-5 w-5 rounded-md bg-vote-no"></div>
                  <span>{{ no }}:</span>
                </div>
                <div>"{{ voteItem.meaningNo }}"</div>
              </div>
              <vote-result :result="votePercentageCount" class="mb-1" />
              <div
                class="flex text-chplus-xs mb-4 text-color-candidate flex-wrap leading-tight"
              >
                <div class="w-full text-left mt-2">
                  <p v-for="vote in otherVoteCount" :key="vote.type">
                    {{ vote.meaning }}: {{ vote.count }}
                  </p>
                </div>
              </div>
              <div v-if="isLoggedIn">
                <p class="text-contrast-light font-bold">
                  {{ $t("views.government.vote.your_vote") }}
                </p>
                <div class="custom-separator bg-interaction-light"></div>
                <div class="flex justify-around mt-5 mb-24">
                  <template v-for="button in buttons">
                    <div
                      class="flex"
                      v-if="!voteResult || voteResult == button.value"
                      :key="button.label"
                    >
                      <vote-button
                        class="uppercase"
                        :decision="button.value"
                        :active="button.value === voteResult"
                        @click="
                          vote(voteResult == button.value ? '' : button.value)
                        "
                      >
                        {{ button.label }}
                      </vote-button>
                    </div>
                  </template>
                </div>
              </div>
              <div
                class="text-chplus-title text-white font-bold relative cursor-pointer"
                style="margin: 0 -0.84rem 0.84rem -0.84rem;"
                @click="toggleParagraph('parliament_votes')"
              >
                <div
                  class="w-full absolute bg-highlight"
                  style="height: 2px; top: 50%; z-index: 0;"
                ></div>
                <div
                  class="rounded-lg relative bg-interaction-dark w-80p mx-auto"
                >
                  <span>{{
                    $t("views.government.vote.parliament_votes")
                  }}</span>
                  <div class="absolute right-0 top-0 pr-2">
                    <icon-angle
                      :direction="
                        hideFullParagraph['parliament_votes'] ? 'down' : 'up'
                      "
                    />
                  </div>
                </div>
              </div>
              <div v-if="!hideFullParagraph['parliament_votes']">
                <box-filter>
                  <button-group>
                    <button
                      @click="clearFilters()"
                      class="button-search flex-0 h-full rounded text-interaction-light button-reset"
                      v-if="hasFilters"
                    >
                      X
                    </button>
                    <button
                      :class="{ active: tab === 'fractions' }"
                      class="button-large"
                      v-if="voteInCurrentLegislature"
                      @click="tab = 'fractions'"
                    >
                      <icon-sort-fractions />
                      {{ $t("views.government.legislative.button_2") }}
                    </button>
                    <button
                      :class="{ active: tab === 'people' }"
                      class="button-large"
                      @click="tab = 'people'"
                    >
                      <icon-sort-people />
                      {{ $t("views.government.legislative.button_3") }}
                    </button>
                    <filter-button
                      @click="showFilter = 'decision'"
                      :active="!!checkedDecision.length"
                    >
                      {{ $t("views.government.vote.votes") }}
                    </filter-button>
                    <button
                      @click="showFilter = 'search'"
                      class="button-search flex-0 h-full rounded"
                      :class="{
                        'bg-interaction-light text-color-regular': !!$query.search,
                        'text-interaction-light': !$query.search
                      }"
                    >
                      <icon-search />
                    </button>
                  </button-group>
                </box-filter>
                <tag-container class="mb-4" v-if="hasFilters">
                  <tag-row>
                    <closeable-search-tag
                      :search="$query.search"
                      @close="$query.search = ''"
                      @open="showFilter = 'search'"
                    />
                  </tag-row>
                  <tag-row>
                    <tag-row>
                      <closeable-decision-tag
                        v-for="decision in checkedDecision"
                        :key="decision"
                        :label="allVotes[decision]"
                        @close="removeDecisionFromFilter(decision)"
                        @open="showFilter = 'decision'"
                      />
                    </tag-row>
                  </tag-row>
                </tag-container>
                <div
                  v-show="tab === 'fractions' && !hasFilters"
                  v-if="voteInCurrentLegislature"
                  class="flex flex-col gap-6"
                >
                  <div
                    class="border-2 border-gray-500 rounded-lg overflow-hidden relative"
                    v-for="group in voteParliamentaryGroups"
                    :id="`group-${group.id}`"
                    :key="group.id"
                  >
                    <group-header
                      :group="group"
                      :totalCouncillors="voteItem.councillors.length"
                      @header-clicked="toggleSubParagraph(`group-${group.id}`)"
                    >
                      <template v-slot:action-right>
                        <!-- Collapse Button -->
                        <button
                          @click="toggleSubParagraph(`group-${group.id}`)"
                        >
                          <icon-angle
                            :direction="
                              showSubParagraph[`group-${group.id}`]
                                ? 'up'
                                : 'down'
                            "
                            class="text-2xl"
                          />
                        </button>
                      </template>
                    </group-header>
                    <div class="w-full px-1">
                      <div class="flex justify-between my-2 uppercase">
                        <div
                          class="w-24 py-1 rounded-lg bg-vote-yes text-vote-yes flex flex-col leading-tight"
                          :class="
                            group.majority === group.percentage.yes
                              ? 'majority'
                              : 'minority'
                          "
                        >
                          <span class="text-chplus-xs">{{
                            $t("views.government.vote.yes_vote")
                          }}</span>
                          <span class="text-chplus-regular"
                            >{{ group.percentage.yes }}%</span
                          >
                        </div>
                        <div
                          class="w-24 py-1 rounded-lg bg-vote-other text-vote-other flex flex-col leading-tight"
                          :class="
                            group.majority === group.percentage.other
                              ? 'majority'
                              : 'minority'
                          "
                        >
                          <span class="text-chplus-xs">{{
                            $t("views.government.vote.other_vote")
                          }}</span>
                          <span class="text-chplus-regular"
                            >{{ group.percentage.other }}%</span
                          >
                        </div>
                        <div
                          class="w-24 py-1 rounded-lg bg-vote-no text-vote-no flex flex-col leading-tight"
                          :class="
                            group.majority === group.percentage.no
                              ? 'majority'
                              : 'minority'
                          "
                        >
                          <span class="text-chplus-xs">{{
                            $t("views.government.vote.no_vote")
                          }}</span>
                          <span class="text-chplus-regular"
                            >{{ group.percentage.no }}%</span
                          >
                        </div>
                      </div>
                      <div
                        class="flex flex-col"
                        v-if="showSubParagraph[`group-${group.id}`]"
                      >
                        <div class="bg-highlight w-full h-px"></div>
                        <council-stats
                          :group="group"
                          :totalCouncillors="voteItem.councillors.length"
                          class="my-3"
                        />
                        <councillor-grid
                          :councillors="group.councillors"
                          small
                          @clickedProfile="
                            ({ councillorId }) =>
                              councillorClick(councillorId, `group-${group.id}`)
                          "
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-show="tab === 'people' || hasFilters"
                  class="flex flex-col px-1 pt-1"
                >
                  <div
                    class="flex flex-col"
                    v-for="{ canton, councillors } in cantonsWithCouncillors"
                    :key="canton.id"
                  >
                    <div
                      class="bg-interaction-dark w-full my-1"
                      :style="{ height: '2px' }"
                    ></div>
                    <div class="w-full text-left flex mb-1">
                      <img
                        :src="canton.coatOfArms || ''"
                        class="coat-of-arms border-interaction-light border"
                      />
                      <span class="uppercase text-chplus-s text-color-regular"
                        >{{ canton.name }}: {{ councillors.length }}</span
                      >
                    </div>
                    <councillor-grid
                      :councillors="councillors"
                      :idGenerator="id => `people-${id}`"
                      @clickedProfile="
                        ({ councillorId }) =>
                          councillorClick(
                            councillorId,
                            `people-${councillorId}`
                          )
                      "
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            class="custom-separator bg-interaction-dark"
            style="margin-top: 3.5rem;"
          ></div>
          <span class="text-chplus-xs">
            {{
              $t("misc.download_source", {
                source: "ws-old.parlament.ch",
                date: new Date(affair.updatedAt).toLocaleDateString()
              })
            }}
          </span>
          <a
            :href="
              $t('views.government.vote.affair_url', {
                id: affair.parliamentId
              })
            "
            target="_blank"
            class="rounded-lg p-3 self-center bg-interaction-dark text-white"
            style="margin-top: 3rem; margin-bottom: 1.25rem"
          >
            {{ $t("views.government.vote.parlament_website") }}
            <icon-link-profile />
          </a>
        </div>
      </div>
    </div>
    <template v-if="isLoading">
      <div class="relative" style="height: 50vh">
        <icon-loading-spinner
          :fade="true"
          color="gray"
          class="absolute-center"
        />
      </div>
    </template>
    <search-filter-modal
      v-if="showFilter == 'search'"
      @validate="onSearchValidate"
      @close="showFilter = ''"
      :value="$query.searchTerm"
    />
    <decision-filter-modal
      v-if="showFilter == 'decision'"
      :checked="checkedDecision"
      @validate="onDecisionValidate"
      @close="showFilter = ''"
    />
    <advanced-check-box-filter-modal
      v-if="showFilter == 'party'"
      :checked="checkedParties"
      @validate="onPartiesValidate"
      @close="showFilter = ''"
      :check-box-values="parties"
      :title="$t('components.buttons.sorting_button.button_party_filter')"
    />
  </layout-envelope>
</template>

<script>
// Utilities
import dayjs from "dayjs";
import client from "api-client";
import { mapGetters } from "vuex";
import { VoteMixin } from "@/utils/VoteMixin";
import IsMobileMixin from "@/utils/IsMobileMixin";
import ShareLinkMixin from "@/utils/ShareLinkMixin";
import { QueryParamMixin } from "@/utils/QueryParam";
import { LanguageCode } from "../../utils/LanguageCode";
import { groupBy } from "@/utils/ArrayUtility";
import StatusBarDirective from "../../directives/StatusBarDirective";

// Components
import LayoutEnvelope from "../../components/layouts/LayoutEnvelope.vue";
import TopSmall from "@/components/layouts/TopSmall";
import BackButtonGovernmentHome from "@/components/buttons/BackButtonGovernmentHome";
import VoteItemTag from "../../components/vote/VoteItemTag.vue";
import NavigationArrow from "@/components/nav/NavigationArrow.vue";
import VoteResult from "../../components/vote/VoteResult.vue";
import BoxFilter from "@/components/layouts/BoxFilter";
import ButtonGroup from "@/components/buttons/ButtonGroup";

// Icons
import IconLoadingSpinner from "@/components/icons/IconLoadingSpinner.vue";
import VoteButton from "../../components/vote/VoteButton.vue";
import IconSearch from "@/components/icons/IconSearch";
import IconLinkProfile from "@/components/icons/IconLinkProfile";
import NavigationGovernment from "../../components/nav/NavigationGovernment.vue";
import FilterButton from "../../components/buttons/filters/FilterButton.vue";
import AdvancedCheckBoxFilterModal from "../../components/modals/AdvancedCheckBoxFilterModal.vue";
import SearchFilterModal from "../../components/modals/SearchFilterModal.vue";
import DecisionFilterModal from "../../components/modals/DecisionFilterModal.vue";
import CloseableSearchTag from "../../components/misc/CloseableSearchTag.vue";
import CloseableDecisionTag from "../../components/misc/CloseableDecisionTag.vue";
import TagContainer from "../../components/misc/TagContainer.vue";
import TagRow from "../../components/misc/TagRow.vue";
import IconAngle from "../../components/icons/IconAngle.vue";
import GroupHeader from "../../components/list/GroupHeader.vue";
import CouncilStats from "../../components/misc/CouncilStats.vue";
import CouncillorGrid from "../../components/people/CouncillorGrid.vue";
import IconSortFractions from "../../components/icons/IconSortFractions.vue";
import IconSortPeople from "../../components/icons/IconSortPeople.vue";

export default {
  name: "Affair",
  components: {
    // Components
    LayoutEnvelope,
    TopSmall,
    BackButtonGovernmentHome,
    VoteItemTag,
    BoxFilter,
    ButtonGroup,
    // Icons
    IconLoadingSpinner,
    CouncillorGrid,
    NavigationArrow,
    NavigationGovernment,
    VoteResult,
    VoteButton,
    IconSearch,
    FilterButton,
    IconLinkProfile,
    AdvancedCheckBoxFilterModal,
    SearchFilterModal,
    CloseableSearchTag,
    TagContainer,
    TagRow,
    IconAngle,
    GroupHeader,
    CouncilStats,
    IconSortFractions,
    IconSortPeople,
    CloseableDecisionTag,
    DecisionFilterModal
  },
  mixins: [VoteMixin, QueryParamMixin, IsMobileMixin, ShareLinkMixin],
  directives: {
    statusbar: StatusBarDirective
  },
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  watch: {
    isLoading() {
      if (!this.isLoading) {
        this.$nextTick(() => {
          if (this.$route.hash) {
            const element = document.querySelector(this.$route.hash);
            if (element) {
              element.scrollIntoView({ behavior: "auto" });
            }
          }
        });
      }
    }
  },
  data() {
    return {
      isLoading: true,
      affair: null,
      voteResults: [],
      councillorPerCantonPerVote: {},
      councillorByNames: {},
      cantonsPerId: {},
      showFilter: "",
      nationalParliamentaryGroups: [],
      buttons: [
        {
          label: this.$t("views.government.vote.yes"),
          value: "Yes"
        },
        {
          label: this.$t("views.government.vote.your_vote_abstain"),
          value: "ES"
        },
        {
          label: this.$t("views.government.vote.no"),
          value: "No"
        }
      ]
    };
  },
  async created() {
    const lang = LanguageCode.get(this.contentLanguage);
    const [
      affair,
      voteResults,
      cantons,
      parliamentParties,
      nationalParliamentaryGroups,
      stateParliamentaryGroups,
      federalCouncillors
    ] = await Promise.all([
      client.fetchAffair(this.id, lang),
      this.isLoggedIn
        ? client.fetchUserVotes(this.user.id)
        : Promise.resolve([]),
      client.fetchCantons(lang),
      client.fetchParliamentaryParties(lang),
      client.fetchParliamentaryGroups(1, lang),
      client.fetchParliamentaryGroups(2, lang),
      client.fetchFederalCouncillors(lang)
    ]);

    this.nationalParliamentaryGroups = nationalParliamentaryGroups;
    const nationalCouncillors = nationalParliamentaryGroups.flatMap(
      group => group.councillors
    );
    const stateCouncillors = stateParliamentaryGroups.flatMap(
      group => group.councillors
    );

    this.parliamentParties = parliamentParties;
    this.cantonsPerId = cantons.reduce(
      (acc, c) => Object.assign(acc, { [c.id]: c }),
      {}
    );

    const councillors = {
      councillor: [...nationalCouncillors, ...stateCouncillors],
      "federal-councillor": federalCouncillors
    };
    for (const [type, councillorList] of Object.entries(councillors)) {
      for (const councillor of councillorList) {
        this.councillorByNames[
          `${councillor.firstName} ${councillor.lastName}`
        ] = {
          ...councillor,
          type,
          link: this.$router.resolve({
            name: type,
            params: { id: Number(councillor.id) }
          }).href
        };
      }
    }

    this.affair = affair;
    this.voteResults = Object.assign({}, ...voteResults);
    this.isLoading = false;
  },
  computed: {
    ...mapGetters("user", ["contentLanguage", "user", "isLoggedIn"]),
    ...mapGetters("viewState", ["currentLegislature"]),
    ...mapGetters("location", ["canton"]),
    background() {
      return this.isMobileScreen
        ? "white"
        : this.canton(this.user.cantonId).backgroundGradient;
    },
    voteInCurrentLegislature() {
      if (!this.voteItem?.date) {
        return false;
      }
      const voteDate = new Date(this.voteItem.date);

      return (
        voteDate >= this.currentLegislature.start &&
        voteDate <= this.currentLegislature.end
      );
    },
    tab: {
      get() {
        if (this.$query.tab) {
          return this.$query.tab;
        }

        return this.voteInCurrentLegislature ? "fractions" : "people";
      },
      set(value) {
        this.$query.tab = value;
      }
    },
    hideFullParagraph: {
      get() {
        if (this.$query.closedParagaphs === undefined) {
          return {};
        }

        const paragraphs = this.$query.closedParagaphs
          ? this.$query.closedParagaphs.split(",")
          : [];
        return paragraphs.reduce((acc, paragraph) => {
          acc[paragraph] = true;
          return acc;
        }, {});
      },
      set(value) {
        this.$query.closedParagaphs = Object.entries(value)
          .filter(([, show]) => show)
          .map(([index]) => index)
          .join(",");
      }
    },
    showSubParagraph: {
      get() {
        const paragraphs = this.$query.openSubParagraphs
          ? this.$query.openSubParagraphs.split(",")
          : [];
        return paragraphs.reduce((acc, paragraph) => {
          acc[paragraph] = true;
          return acc;
        }, {});
      },
      set(value) {
        this.$query.openSubParagraphs = Object.entries(value)
          .filter(([, show]) => show)
          .map(([index]) => index)
          .join(",");
      }
    },
    checkedDecision: {
      get() {
        return this.$query.decisions ? this.$query.decisions.split(",") : [];
      },
      set(value) {
        this.replaceQuery(
          {
            ...this.$query,
            decisions: value.join(",")
          },
          ""
        );
      }
    },
    hasFilters() {
      return this.checkedDecision.length > 0 || !!this.$query.search;
    },
    voteParliamentaryGroups() {
      const decisionsPerId = this.voteItem.councillors.reduce(
        (acc, councillor) =>
          Object.assign(acc, { [councillor.id]: councillor.decision }),
        {}
      );
      const filteredGroups = this.nationalParliamentaryGroups.map(group => ({
        ...group,
        councillors: group.councillors
          .filter(councillor => councillor.id in decisionsPerId)
          .map(councillor => ({
            ...councillor,
            decision: decisionsPerId[councillor.id]
          }))
      }));

      return filteredGroups.map(group => {
        const noCount = group.councillors.filter(
          councillor => councillor.decision === "No"
        ).length;
        const noPercentage = Math.floor(
          (noCount / group.councillors.length) * 100
        );

        const yesCount = group.councillors.filter(
          councillor => councillor.decision === "Yes"
        ).length;
        const yesPercentage = Math.floor(
          (yesCount / group.councillors.length) * 100
        );
        const otherPercentage = 100 - noPercentage - yesPercentage;
        group.percentage = {
          no: noPercentage,
          yes: yesPercentage,
          other: otherPercentage
        };
        group.majority = Math.max(noPercentage, yesPercentage, otherPercentage);
        return group;
      });
    },
    councillors() {
      return [...(this.voteItem?.councillors || [])].sort((p1, p2) => {
        const party1 = p1.parliamentPartyId || 0;
        const party2 = p2.parliamentPartyId || 0;
        if (party1 === party2) {
          return p1.lastName.localeCompare(p2.lastName);
        } else {
          return party2 - party1;
        }
      });
    },
    filteredCouncillors() {
      const search = (this.$query.search || "").toLocaleLowerCase();
      const decisions = new Set(this.checkedDecision);
      return this.councillors
        .filter(
          councillor =>
            decisions.size == 0 || decisions.has(councillor.decision)
        )
        .filter(
          councillor =>
            councillor.firstName.toLocaleLowerCase().includes(search) ||
            councillor.lastName.toLocaleLowerCase().includes(search)
        );
    },
    filteredCouncillorsPerCanton() {
      return groupBy(this.filteredCouncillors, c => c.cantonId);
    },
    voteCount() {
      return this.voteItem.voteAggregation.counts;
    },
    yesCount() {
      return this.voteCount?.find(count => count.type == 1)?.count || 0;
    },
    noCount() {
      return this.voteCount?.find(count => count.type == 2)?.count || 0;
    },
    councillorsPartyId() {
      return new Set(this.councillors.map(c => c.parliamentPartyId));
    },
    parties() {
      return this.parliamentParties
        .filter(party => this.councillorsPartyId.has(party.id))
        .map(p => ({
          value: `${p.id}`,
          label: p.name
        }));
    },
    otherVoteCount() {
      return this.voteCount.filter(count => count.type > 2);
    },
    votePercentageCount() {
      return {
        yes: {
          count: this.yesCount,
          percentage: this.voteItem.voteAggregation.percents.yes
        },
        no: {
          count: this.noCount,
          percentage: this.voteItem.voteAggregation.percents.no
        },
        other: {
          count: this.otherVoteCount.reduce(
            (acc, count) => acc + count.count,
            0
          ),
          percentage: this.voteItem.voteAggregation.percents.other
        }
      };
    },
    yes() {
      return this.$t("views.government.vote.yes");
    },
    no() {
      return this.$t("views.government.vote.no");
    },
    formattedId() {
      const id = new String(this.affair?.parliamentId || "");
      return `${id.slice(2, 4)}.${id.slice(5)}`;
    },
    voteIndex() {
      const index = this.affair.votes.findIndex(
        v => v.id == this.$query.voteId
      );
      return index === -1 ? 0 : index;
    },
    voteResult() {
      return this.voteResults[this.voteItem.id] || "";
    },
    voteItem() {
      return this.affair?.votes[this.voteIndex];
    },
    cantonsWithCouncillors() {
      const cantonsWithCouncillors = Object.entries(
        this.filteredCouncillorsPerCanton
      ).map(([cantonId, councillors]) => ({
        canton: this.cantonsPerId[cantonId],
        councillors: councillors
      }));

      cantonsWithCouncillors.sort(
        ({ canton: canton1 }, { canton: canton2 }) => {
          if (canton1.id === this.user.cantonId) {
            canton1 = { name: "" };
          }
          if (canton2.id === this.user.cantonId) {
            canton2 = { name: "" };
          }
          return canton1.name.localeCompare(canton2.name);
        }
      );

      return cantonsWithCouncillors;
    },
    paragraphs() {
      return [
        {
          title: this.$t("views.government.vote.initial_situation"),
          content: this.affair.initialSituation,
          class: "affair-initial-situation"
        },
        {
          title: this.$t("views.government.vote.submitted_text"),
          content: this.affair.submittedText,
          class: "affair-submitted-text"
        },
        {
          title: this.$t("views.government.vote.proceedings"),
          content: this.affair.proceedings,
          class: "affair-proceedings"
        },
        {
          title: this.$t("views.government.vote.justification"),
          content: this.affair.justification,
          class: "affair-justification"
        },
        {
          title: this.$t("views.government.vote.answer_br_office"),
          content: this.affair.answerBROffice,
          class: "affair-answer-br-office"
        }
      ].filter(p => !!p.content);
    },
    prettyParagraphs() {
      return this.paragraphs.map(p => ({
        ...p,
        content: this.prettifyHtml(
          p.content,
          p.class,
          p.class !== "affair-proceedings"
        )
      }));
    },
    debugEnabled() {
      return process.env.VUE_APP_DEBUG_ENABLED === "true";
    }
  },
  methods: {
    toggleSubParagraph(index) {
      this.showSubParagraph = {
        ...this.showSubParagraph,
        [index]: !this.showSubParagraph[index]
      };
    },
    toggleParagraph(index) {
      this.hideFullParagraph = {
        ...this.hideFullParagraph,
        [index]: !this.hideFullParagraph[index]
      };
    },
    prettifyHtml(htmlString, id, allInOneParagraph = false) {
      const input = document.createElement("div");
      input.innerHTML = htmlString;

      const output = [];

      const createElement = (tag, styleClass = "", text = "") => {
        const element = document.createElement(tag);
        if (styleClass) element.classList.add(styleClass);
        element.innerText = text;
        return element;
      };

      let paragraph = null;
      const addToParagraph = element => {
        if (!paragraph) {
          paragraph = createElement("div", "affair-paragraph");
          paragraph.id = `${id}-${output.length}`;
          output.push(paragraph);
        }
        paragraph.appendChild(element);
      };
      let previousWasHeader = false;
      for (const child of input.children) {
        const trimmedContent = child.textContent.trim();
        if (
          trimmedContent === "" ||
          trimmedContent.match(
            /^(?:SDA-Meldung|Dépêche ATS|(?:Projet|Entwurf) \d+(?:.*?\d+)?)$/
          )
        ) {
          continue;
        }

        if (child.tagName === "A" || child.children[0]?.tagName === "A") {
          const a = child.tagName === "A" ? child : child.children[0];
          if (a.href.match(/^https?:\/\/(?:www\.)?parlament\.ch/)) {
            const linkContainer = createElement("div", "affair-paragraph-link");
            const link = createElement(
              "a",
              "",
              this.$t("views.government.vote.parlament_website") +
                ": " +
                a.innerText
            );
            link.target = "_blank";
            link.href = a.href;
            linkContainer.appendChild(link);
            addToParagraph(linkContainer);
          }
          previousWasHeader = false;
        } else if (
          !previousWasHeader &&
          (["SPAN", "STRONG"].includes(child.tagName) ||
            (child.children.length === 1 &&
              child.children[0].tagName === "B" &&
              child.textContent.length < 150))
        ) {
          // Headers are always <span> or <strong>
          if (!allInOneParagraph || !paragraph) {
            paragraph = createElement("div", "affair-paragraph");
            paragraph.id = `${id}-${output.length}`;
            output.push(paragraph);
          }

          const header = createElement(
            "p",
            "affair-paragraph-header",
            child.textContent
          );
          paragraph.appendChild(header);
          previousWasHeader = true;
        } else if (
          (!trimmedContent.endsWith(".") && trimmedContent.length < 150) ||
          (child.children.length === 3 && child.children[1].tagName === "BR")
        ) {
          // If the text is too long, we split it into a title and a content
          if (child.children.length <= 1 && child.textContent.length > 150) {
            const index = child.textContent.indexOf(".");
            if (index > 0) {
              const content = createElement(
                "p",
                "",
                child.textContent.slice(index + 1)
              );
              if (child.children.length == 0) {
                child.appendChild(document.createElement("p"));
              }
              child.children[0].innerText = child.textContent.slice(0, index);
              child.appendChild(document.createElement("br"));
              child.appendChild(content);
            }
          }

          const titleElement =
            child.children.length >= 3 ? child.children[0] : child;
          const title = createElement(
            "p",
            "affair-paragraph-title",
            titleElement.textContent
          );

          addToParagraph(title);

          // If the child has children, it's a title followed by a separator and a content
          if (child.children.length >= 3) {
            const content = createElement(
              "p",
              "affair-paragraph-content",
              child.children[2].textContent
            );
            paragraph.appendChild(content);
          }
          previousWasHeader = false;
        } else {
          // By default the child is a content
          const content = createElement(
            "p",
            "affair-paragraph-content",
            child.textContent
          );

          addToParagraph(content);
          previousWasHeader = false;
        }
      }

      const councillorNames = Object.keys(this.councillorByNames)
        .map(name => `${name}`)
        .join("|");
      const councillorRegexp = new RegExp(
        `(${councillorNames})(?:\\s\\(.*?\\))?`,
        "g"
      );

      const councillorsIds = {};
      const getNextId = councillor => {
        if (!councillorsIds[councillor.id]) {
          councillorsIds[councillor.id] = 0;
        }
        return `link-${councillor.firstName}-${
          councillor.lastName
        }-${councillorsIds[councillor.id]++}`.replace(/\W+/g, "-");
      };

      const contentCouncillorsList = [];
      for (const paragraph of output) {
        for (const child of paragraph.children) {
          if (child.classList.contains("affair-paragraph-content")) {
            const contentCouncillors = {
              paragraph,
              content: child,
              councillors: []
            };
            for (const match of child.textContent.matchAll(councillorRegexp)) {
              const councillor = this.councillorByNames[match[1]];
              if (councillor) {
                contentCouncillors.councillors.push(councillor);
                const councillorElement = createElement(
                  "a",
                  "font-bold",
                  match[0]
                );
                councillorElement.style.color = councillor.partyColor;
                councillorElement.href = councillor.link;
                councillorElement.id = getNextId(councillor);
                councillorElement.dataset.id = councillor.id;
                councillorElement.dataset.type = councillor.type;
                child.innerHTML = child.innerHTML.replace(
                  match[0],
                  councillorElement.outerHTML
                );
              }
            }
            contentCouncillorsList.push(contentCouncillors);
          }
        }
      }

      for (const {
        paragraph,
        content,
        councillors
      } of contentCouncillorsList) {
        if (councillors.length > 0) {
          const councillorList = createElement(
            "div",
            "affair-paragraph-people"
          );
          for (const councillor of councillors) {
            const councillorPicture = createElement("img");
            councillorPicture.src = councillor.urlImage;
            councillorPicture.style.color = councillor.partyColor;

            const councillorLink = createElement("a");
            councillorLink.href = councillor.link;
            councillorLink.dataset.id = councillor.id;
            councillorLink.dataset.type = councillor.type;

            councillorLink.id = getNextId(councillor);

            councillorLink.appendChild(councillorPicture);

            councillorList.appendChild(councillorLink);
          }
          paragraph.insertBefore(councillorList, content);
        }
      }

      for (const paragraph of output) {
        if (paragraph.textContent.length < 150) {
          continue;
        }
        const more = createElement("span", "affair-more");
        more.innerText =
          localStorage.getItem("locale") === "fr"
            ? "Voir plus..."
            : "Mehr anzeigen...";

        paragraph.appendChild(more);
      }

      return output;
    },
    clearFilters() {
      const newQuery = { ...this.$query };
      delete newQuery["decisions"];
      delete newQuery["search"];
      this.replaceQuery(newQuery);
    },
    formattedDate(date) {
      return dayjs(date).format("DD.MM.YYYY");
    },
    vote(decision) {
      this.voteResults = {
        ...this.voteResults,
        [this.voteItem.id]: decision
      };

      const request = decision
        ? client.createUserVote(this.user.id, this.voteItem.id, decision)
        : client.deleteUserVote(this.user.id, this.voteItem.id);
      request.catch(() => {
        const voteResults = { ...this.voteResults };
        delete voteResults[this.voteItem.id];
        this.voteResults = voteResults;
      });
    },
    councillorClick(id, divId) {
      this.$router
        .replace({
          ...this.$route,
          hash: `#${divId}`
        })
        .finally(() => {
          this.$router.push({
            name: "councillor",
            params: {
              id: Number(id)
            }
          });
        });
    },
    nextVote() {
      const nextId = this.affair.votes[
        (this.voteIndex + 1) % this.affair.votes.length
      ].id;
      this.replaceQuery(
        {
          ...this.$query,
          voteId: nextId
        },
        ""
      );
    },
    previousVote() {
      const nextId = this.affair.votes[
        (this.voteIndex - 1 + this.affair.votes.length) %
          this.affair.votes.length
      ].id;
      this.replaceQuery(
        {
          ...this.$query,
          voteId: nextId
        },
        ""
      );
    },
    toggleShow(event) {
      if (["A", "IMG"].includes(event.target.tagName)) {
        const link =
          event.target.tagName === "A"
            ? event.target
            : event.target.parentElement;

        if (link.dataset.id) {
          event.stopPropagation();
          event.preventDefault();
          this.$router
            .replace({
              ...this.$route,
              hash: `#${link.id}`
            })
            .finally(() => {
              this.$router.push({
                name: link.dataset.type,
                params: {
                  id: Number(link.dataset.id)
                }
              });
            });
        }
        return;
      }

      const paragraph = event.target.closest(".affair-paragraph");
      if (!paragraph) {
        return;
      }

      this.showSubParagraph = {
        ...this.showSubParagraph,
        [paragraph.id]: !this.showSubParagraph[paragraph.id]
      };
      if (this.showSubParagraph[paragraph.id]) {
        this.$nextTick(() => {
          const container = document.querySelector(".scroll-content");
          const header = document.getElementById(paragraph.id);
          if (header) {
            const rem = parseFloat(
              getComputedStyle(document.documentElement).fontSize
            );
            const containerHeight = container.getBoundingClientRect().height;
            const height = 19.65625 * rem;
            container.scrollTo({
              top: header.offsetTop - parseInt(containerHeight / 2 - height / 2)
            });
          }
        });
      }
    },
    onDecisionValidate(newValues) {
      this.checkedDecision = newValues;
      this.showFilter = "";
    },
    onSearchValidate(newValue) {
      this.$query.search = newValue;
      this.showFilter = "";
    },
    removeDecisionFromFilter(decision) {
      this.checkedDecision = this.checkedDecision.filter(d => d != decision);
    },
    share() {
      this.shareCurrentLink(
        this.$t("views.government.vote.share_subject"),
        this.$t("views.government.vote.share_message", {
          title: this.affair.title
        })
      );
    }
  }
};
</script>
<style lang="scss">
.coat-of-arms {
  height: 23px;
  margin-right: 0.5em;
  border-radius: 100%;
}

.sorting-button-overlays-inner-scroll {
  max-height: 62vh;
  overflow-y: scroll;
}

.w-80p {
  width: 80%;
}

.custom-separator {
  height: 2px !important;
  min-height: 2px;
  width: 80%;
  margin-left: auto;
  margin-right: auto;
}

.vote-item-title {
  font-weight: bold;
}

.affair-paragraph-separator {
  height: 1rem;
  width: 6px;
  background-color: var(--highlight);
  margin: 0 auto;
}

.affair-proceedings .affair-paragraph {
  border-color: var(--highlight);
  background-color: white;
}

.affair-paragraph {
  padding: 1rem;
  border-radius: 1rem;
  border-width: 6px;
  display: flex;
  border-color: transparent;
  flex-direction: column;
  row-gap: 0.5rem;
  position: relative;
  overflow: hidden;
  text-align: left;

  & .affair-paragraph-header {
    font-size: 0.8625rem;
    font-weight: bold;
  }

  & .affair-paragraph-title {
    font-weight: bold;

    &:not(:nth-child(2)) {
      margin-top: 1rem;
    }
  }

  & .affair-paragraph-link a {
    border-bottom: 1px solid var(--interaction-dark);
  }
}

.affair-more {
  display: none;
}

.show-excerpt .affair-more {
  display: block;
  font-size: 0.8625rem;
  color: var(--interaction-dark);
}

.affair-paragraph-people {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  overflow: hidden;
  justify-content: start;

  & > a > img {
    width: 2.625rem;
    height: 2.625rem;
    border-radius: 100%;
    border: 2px solid;
  }
}

.show-excerpt .affair-paragraph-content {
  max-height: 7.65625rem;
  text-overflow: ellipsis;
  overflow: hidden;
}

.show-excerpt .affair-paragraph-content ~ *:not(.affair-more) {
  display: none;
}

.vote-switch {
  border: 2px solid var(--interaction-light);
}

.yesno-container {
  display: grid;
  grid-template-columns: auto auto;
  grid-template-rows: auto auto;
  column-gap: 1rem;
  row-gap: 0.5rem;
}

.minority {
  background-color: transparent !important;
}

.majority {
  color: white !important;
}
</style>
